import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, isSupervisor } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import { Loading } from "@/components/common";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Home from "@/pages/Home";
import Take5New from "@/pages/Take5New";
import Take5Page from "@/pages/Take5Page";
import EquipmentList from "@/pages/EquipmentList";
import EquipmentDetail from "@/pages/EquipmentDetail";
import InspectionPage from "@/pages/InspectionPage";
import Activities from "@/pages/Activities";
import Supervision from "@/pages/Supervision";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import Teams from "@/pages/Teams";
import Platform from "@/pages/Platform";
import Journal from "@/pages/Journal";
import InfoPage from "@/pages/InfoPage";
import { ConsentBanner } from "@/components/ConsentBanner";
import { ChangePassword } from "@/pages/Profile";

const ForcePassword = () => {
  const { reload, logout } = useAuth();
  return (
    <div className="mx-auto max-w-xl space-y-4 p-6 pt-12" data-testid="force-password-page">
      <h1 className="font-heading text-3xl font-bold">Choisissez votre mot de passe</h1>
      <p className="text-muted-foreground">Votre compte a été créé avec un mot de passe provisoire. Remplacez-le pour accéder à Safe Start.</p>
      <ChangePassword title="Nouveau mot de passe" currentLabel="Mot de passe provisoire (reçu par e-mail)" onDone={reload} />
      <button type="button" onClick={logout} data-testid="force-password-logout" className="text-sm text-muted-foreground underline">Se déconnecter</button>
    </div>
  );
};

const Protected = ({ children, allow }) => {
  const { user } = useAuth();
  if (user === null) return <div className="p-8"><Loading /></div>;
  if (!user) return <Navigate to="/connexion" replace />;
  if (user.must_change_password) return <ForcePassword />;
  if (allow && !allow(user)) return <Navigate to="/" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="p-8"><Loading /></div>;
  if (user) return <Navigate to="/" replace />;
  return children;
};

const ThemedToaster = () => <Toaster position="top-center" richColors theme={useTheme().theme} />;

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/connexion" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/inscription" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/mot-de-passe-oublie" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reinitialiser" element={<ResetPassword />} />
            <Route path="/infos/:slug" element={<InfoPage />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route index element={<Home />} />
              <Route path="take5/nouveau" element={<Protected allow={(u) => u.role !== "controleur"}><Take5New /></Protected>} />
              <Route path="take5/:id" element={<Take5Page />} />
              <Route path="equipements" element={<EquipmentList />} />
              <Route path="equipements/:id" element={<EquipmentDetail />} />
              <Route path="inspections/:id" element={<InspectionPage />} />
              <Route path="activites" element={<Activities />} />
              <Route path="profil" element={<Profile />} />
              <Route path="equipes" element={<Teams />} />
              <Route path="supervision" element={<Protected allow={isSupervisor}><Supervision /></Protected>} />
              <Route path="admin" element={<Protected allow={(u) => u.role === "admin" || u.platform_admin}><Admin /></Protected>} />
              <Route path="plateforme" element={<Protected allow={(u) => u.platform_admin}><Platform /></Protected>} />
              <Route path="journal" element={<Protected allow={(u) => u.role === "controleur" || u.role === "admin"}><Journal /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ConsentBanner />
        </BrowserRouter>
        <ThemedToaster />
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
